import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { DateUtils, LocalComponentStore, RxjsUtils } from "@dtm-frontend/shared/utils";
import { combineLatestWith, map } from "rxjs";
import {
    BasicQualification,
    OperationScenarioCategory,
    OperatorAndPilotQualification,
    QualificationStatus,
} from "../../models/operator.models";

type Qualification = BasicQualification | OperatorAndPilotQualification;

interface QualificationBadgeComponentState {
    qualification: Qualification | undefined;
    closeExpirationPeriodDays: number | undefined;
}

@Component({
    selector: "dtm-ui-qualification-badge",
    templateUrl: "./qualification-badge.component.html",
    styleUrls: ["./qualification-badge.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LocalComponentStore],
})
export class QualificationBadgeComponent {
    @Input({ required: true }) public set qualification(value: Qualification | undefined) {
        this.localStore.patchState({ qualification: value });
    }

    @Input() public set closeExpirationPeriodDays(value: number | undefined) {
        this.localStore.patchState({ closeExpirationPeriodDays: value });
    }

    protected readonly qualification$ = this.localStore.selectByKey("qualification").pipe(RxjsUtils.filterFalsy());
    protected readonly closeExpirationPeriodDays$ = this.localStore.selectByKey("closeExpirationPeriodDays");
    protected readonly isExpired$ = this.qualification$.pipe(map((qualification) => this.isQualificationExpired(qualification)));
    protected readonly isAboutToExpire$ = this.qualification$.pipe(
        combineLatestWith(this.closeExpirationPeriodDays$),
        map(([qualification, closeExpirationPeriodDays]) => this.isQualificationAboutToExpire(qualification, closeExpirationPeriodDays))
    );
    protected readonly OperationScenarioCategory = OperationScenarioCategory;
    protected readonly QualificationStatus = QualificationStatus;

    constructor(private readonly localStore: LocalComponentStore<QualificationBadgeComponentState>) {
        this.localStore.setState({
            qualification: undefined,
            closeExpirationPeriodDays: undefined,
        });
    }

    private isQualificationExpired(qualification: Qualification) {
        const currentDate = new Date();

        return qualification.expirationDate < currentDate;
    }

    private isQualificationAboutToExpire(qualification: Qualification, period: number | undefined) {
        if (!period) {
            return false;
        }

        return !this.isQualificationExpired(qualification) && qualification.expirationDate < DateUtils.addDays(new Date(), period);
    }
}
